import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { brokerQuotes, type OperationDetail } from '@/shared/api/resources';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

/**
 * "Cancelar cotacao" da corretora (NCT-018 e NCT-077): a confirmacao do OnPoint ("Essa
 * acao tem efeito permanente, deseja continuar?") e o motivo, opcional. Sem motivo, o
 * servidor grava o do OnPoint, "Cancelamento via clique do usuario".
 */
export function QuoteWithdrawDialog({
  operationId,
  open,
  onClose,
  onDone,
}: {
  operationId: string;
  open: boolean;
  onClose: () => void;
  onDone?: ((operacao: OperationDetail) => void) | undefined;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [motivo, setMotivo] = useState('');

  const cancelar = useMutation({
    mutationFn: () => brokerQuotes.withdraw(operationId, motivo.trim()),
    onSuccess: (operacao) => {
      toast.success('Cotacao cancelada.');
      setMotivo('');
      void queryClient.invalidateQueries({ queryKey: ['operations'] });
      void queryClient.invalidateQueries({ queryKey: ['quote-timeline', operationId] });
      onDone?.(operacao);
      onClose();
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Cancelar cotacao"
      description="Essa acao tem efeito permanente, deseja continuar?"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={cancelar.isPending}>
            Voltar
          </Button>
          <Button
            variant="danger"
            loading={cancelar.isPending}
            onClick={() => cancelar.mutate()}
          >
            Cancelar cotacao
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {cancelar.isError ? (
          <Alert tone="danger" title="Nao foi possivel cancelar a cotacao">
            {cancelar.error.message}
          </Alert>
        ) : null}
        <Textarea
          label="Motivo do cancelamento"
          rows={3}
          maxLength={2000}
          value={motivo}
          placeholder="Descreva o motivo do cancelamento"
          onChange={(event) => setMotivo(event.target.value)}
        />
      </div>
    </Modal>
  );
}
